import React, { useEffect, useState } from "react";
import axios from "axios";
import { PageHeader, Panel, Field } from "../components/UI";
import ReadOnlyField from "../components/ReadOnlyField";
import ScenarioInformation from "../components/ScenarioInformation";
import WorkshopBrowser from "../components/WorkshopBrowser";
import { LauncherHeading, LauncherSummary, ReadOnlyBadge } from "../components/LauncherUI";

const API = "";

export default function StartupView({ setRestartRequired }) {

  const [maxFPS, setMaxFPS] = useState("");
  const [loadSessionSave, setLoadSessionSave] = useState(false);
  const [configFile, setConfigFile] = useState("");
  const [saved, setSaved] = useState(false);
  const [configuration, setConfiguration] = useState({ status: "loading", data: null });

  const [scenarioId, setScenarioId] = useState("");
  const [scenarioEdited, setScenarioEdited] = useState(false);
  useEffect(() => {
    axios.get(`${API}/api/config`)
      .then(res => {
        setConfiguration({ status: "loaded", data: res.data });
        setScenarioId(res.data.game?.scenarioId ?? "");
      })
      .catch(() => setConfiguration({ status: "error", data: null }));
  }, []);

  useEffect(() => {
    axios.get(`${API}/api/startup`)
      .then(res => {
        setMaxFPS(res.data.maxFPS || "");
        setLoadSessionSave(res.data.loadSessionSave || false);
        setConfigFile(res.data.configFile || "config.json");
      })
      .catch(() => alert("Failed to load startup parameters."));
  }, []);

  const save = async () => {
    try {
      if (scenarioEdited) {
        const { data: currentConfig } = await axios.get(`${API}/api/config`);
        await axios.post(`${API}/api/config`, {
          ...currentConfig,
          game: { ...currentConfig.game, scenarioId }
        });
      }
      await axios.post(`${API}/api/startup`, {
        maxFPS: maxFPS ? parseInt(maxFPS) : null,
        loadSessionSave,
        configFile
      });
      setSaved(true);
      if (setRestartRequired) setRestartRequired(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      alert("Failed to save startup parameters.");
    }
  };

  const startupCommand = `./steamInstall.sh
./ArmaReforgerServer -config ${configFile} -profile /home/jeff/arma -maxFPS ${(maxFPS ? parseInt(maxFPS) : null) || 80}${loadSessionSave ? " -loadSessionSave" : ""}`;

  const config = configuration.data;
  const game = config?.game;
  const properties = game?.gameProperties;
  const fallback = configuration.status === "loading" ? "Loading…"
    : configuration.status === "error" ? "Unavailable" : "Not set";
  // Display-only name; preserve the original Scenario ID and configuration.
  const missionName = typeof scenarioId === "string"
    ? scenarioId.replace(/^\{[^}]*\}/, "").split(/[\\/]/).pop()
      .replace(/\.conf$/i, "").replace(/[_-]+/g, " ").trim() || fallback
    : fallback;

  return (
    <div className="page-stack mission-launcher">
      <PageHeader title="Mission Launcher" description="Server configuration, mission and startup parameters." />
      {configuration.status === "error" && (
        <p className="muted" role="status">Configuration could not be loaded. Startup parameters can still be saved.</p>
      )}

      <LauncherSummary serverName={game?.name || fallback} missionName={missionName}
        maxPlayers={game?.maxPlayers ?? fallback}
        installedMods={Array.isArray(game?.mods) ? game.mods.length : fallback}
        configFile={configFile || "Not set"} />

      <div className="launcher-main-grid">
      <Panel title={<LauncherHeading icon="🖥">General</LauncherHeading>} description="Server and network configuration · Read-only" className="launcher-general-panel">
        <section className="launcher-general-group">
          <h2>Server</h2>
          <div className="launcher-fields">
            <ReadOnlyField label="Server Name" id="server-name" value={game?.name} fallback={fallback} />
            <ReadOnlyField label="Max Players" id="max-players" value={game?.maxPlayers} fallback={fallback} />
            <ReadOnlyField label="Admin Password" id="admin-password" value={game?.passwordAdmin} fallback={fallback} masked />
          </div>
        </section>
        <section className="launcher-general-group">
          <h2>Network</h2>
          <div className="launcher-fields">
            <ReadOnlyField label="Public Address" id="public-address" value={config?.publicAddress} fallback={fallback} />
            <ReadOnlyField label="Game Port" id="game-port" value={config?.publicPort} fallback={fallback} />
            <ReadOnlyField label="RCON Port" id="rcon-port" value={config?.rcon?.port} fallback={fallback} />
          </div>
        </section>
      </Panel>

      <Panel title={<LauncherHeading icon="🚀">Launch Options</LauncherHeading>} description="Edit startup parameters. Configuration flags are read-only." className="launcher-launch-options-panel">
        <div className="launcher-options-row">
          {/* Max FPS */}
          <Field label="Max FPS" htmlFor="max-fps" hint="The server uses 80 FPS when no value is set.">
            <input id="max-fps" type="number" value={maxFPS}
              onChange={e => setMaxFPS(e.target.value)} className="input-short" />
          </Field>
          {/* Load Session Save */}
          <label className="checkbox-field">
            <input type="checkbox" checked={loadSessionSave}
              onChange={e => setLoadSessionSave(e.target.checked)} />
            <span>Load Session Save</span>
          </label>
          <ReadOnlyBadge label="BattlEye" id="battleye" value={properties?.battlEye} fallback={fallback} />
          <ReadOnlyBadge label="Cross Platform" id="cross-platform" value={game?.crossPlatform} fallback={fallback} />
          <ReadOnlyBadge label="Fast Validation" id="fast-validation" value={properties?.fastValidation} fallback={fallback} />
        </div>
      </Panel>

      <Panel title={<LauncherHeading icon="🗺">Mission</LauncherHeading>} description="Current scenario from the server configuration" className="launcher-mission-panel">
        <div className="launcher-mission-grid">
          <div className="launcher-scenario">
            <p className="eyebrow">CURRENT MISSION</p>
            <h3 className="launcher-mission-name">{missionName}</h3>
            <Field label="Scenario ID" htmlFor="scenario-id">
              <input id="scenario-id" type="text" className="launcher-scenario-value"
                value={scenarioId} placeholder={fallback}
                disabled={configuration.status !== "loaded"}
                onChange={e => {
                  setScenarioId(e.target.value);
                  setScenarioEdited(true);
                }} />
            </Field>
            <ScenarioInformation scenarioId={scenarioId} />
            <WorkshopBrowser disabled={configuration.status !== "loaded"} onModsImported={(updatedConfig, importedCount) => {
              setConfiguration(previous => ({
                ...previous,
                data: {
                  ...previous.data,
                  game: { ...previous.data.game, mods: updatedConfig.game.mods }
                }
              }));
              if (importedCount && setRestartRequired) setRestartRequired(true);
            }} onSelect={value => {
              setScenarioId(value);
              setScenarioEdited(true);
            }} />
          </div>
          <Field label="Mission/Scenario selector" htmlFor="mission-selector" hint="Scenario browsing is not available yet.">
            <select id="mission-selector" className="launcher-placeholder" disabled defaultValue="">
              <option value="">Scenario selection — coming later</option>
            </select>
          </Field>
        </div>
        {/* Config File */}
        <div className="launcher-config-file">
          <Field label="Config File" htmlFor="config-file" hint="Configuration file used by the startup command.">
            <input id="config-file" type="text" value={configFile}
              onChange={e => setConfigFile(e.target.value)} />
          </Field>
        </div>
      </Panel>

      </div>

      <Panel title={<LauncherHeading icon="💻">Launch Command Preview</LauncherHeading>} description="Read-only preview of the generated startup command. Save to apply changes.">
        <div className="launcher-terminal">
          <div className="launcher-terminal-header"><span aria-hidden="true">&gt;_</span> Startup command <span className="launcher-terminal-mode">Read-only</span></div>
          <pre className="command-preview" aria-label="Generated startup command"><code>{startupCommand}</code></pre>
        </div>
        {/* Save Button */}
        <div className="form-footer">
          <button onClick={save}>Save Startup Parameters</button>
          {saved && <span className="save-feedback" role="status">✓ Saved (restart required)</span>}
        </div>
      </Panel>
    </div>
  );
}
